import { useMemo, useState } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useAgentWorkspaceFiles } from '../../../lib/agent-workspace-files';
import { type BrowsableFile, folderPathTitle } from '../../../lib/file-browser';
import { Modal } from '../../Modal';
import { Button, Typography } from '../ui';
import { FolderBrowseList } from './FolderBrowseList';
import styles from './NewFileDialog.module.css';

// "Where does this go" as a dialog of its own, answering with a folder and doing nothing else.
//
// ONE component for every surface that asks it — the upload destination and the editor's Location row —
// because it is one question, and this repo has a standing rule about a second list that looks like the
// first. (It was very nearly two: this started as a copy of `UploadDestinationDialog`, which is the same
// modal around the same `FolderBrowseList`. The copy is gone; the caller varies the words.)
//
// `FolderPickerDialog` beside it is deliberately NOT folded in: that one MOVES a file it is handed, so it
// needs an item and it writes. This one is a question. They share `FolderBrowseList`, so the tree, the
// breadcrumb and the Space-vs-folder distinction cannot drift between them.
//
// It reads the cross-workspace list itself, for the reason the move dialog documents: a caller-supplied
// list would shrink the tree depending on which screen opened it.
export function FolderChooserDialog({
  startFolder = '',
  title,
  description,
  confirmLabel,
  onClose,
  onChoose,
}: {
  /** Where the tree opens. `''` is the top, whose rows are Spaces plus the account roots. */
  startFolder?: string;
  title: string;
  description?: string;
  confirmLabel: string;
  /** Dismissed without choosing. */
  onClose: () => void;
  onChoose: (folder: string) => void;
}) {
  const { t } = useTranslation();
  const state = useAgentWorkspaceFiles();
  const all: BrowsableFile[] = state.status === 'ready' ? state.items : [];
  const workspaceNames = useMemo(
    () => Object.fromEntries(state.status === 'ready' ? state.items.map((file) => [file.workspaceId, file.workspaceName]) : []),
    [state],
  );

  const [folder, setFolder] = useState(startFolder);

  /** The chosen path as a reader would say it — the Space by name, the account roots translated. */
  const label = (target: string) => {
    return folderPathTitle(target, workspaceNames, t);
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="minimum"
      maxHeightPx={650}
      scrollBody
      headerSeparator
      footerSeparator
      title={title}
      description={description}
      footer={
        <>
          <Button variant="outlined" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          {/* A Space is a valid destination; the only thing that blocks is not having picked anything. */}
          <Button onClick={() => onChoose(folder)} disabled={folder === ''}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div className={styles.body}>
        <FolderBrowseList
          folder={folder}
          onNavigate={setFolder}
          files={all}
          workspaceNames={workspaceNames}
          loading={state.status === 'loading'}
        />
        {/* Says what pressing the button will actually choose — the tree shows where you ARE, which at a
            Space root looks the same as having chosen nothing. */}
        <Typography variant="p3" color="secondary" component="p">
          {folder === '' ? t('files.move.pickWorkspace') : t('files.new.willPutIn', { folder: label(folder) })}
        </Typography>
      </div>
    </Modal>
  );
}
